<!--
  ReviewTestModal.vue
  후기 생성 테스트 모달 컴포넌트
-->
<template>
  <a-modal
    :open="open"
    title="후기 생성 테스트"
    :confirm-loading="loading"
    :footer="null"
    @cancel="handleCancel"
    width="900px"
  >
    <div class="test-form">
      <div class="form-row">
        <div class="form-group">
          <label>프롬프트 선택</label>
          <a-select 
            v-model:value="form.selectedPromptId" 
            style="width: 100%" 
            @change="handlePromptSelect"
          >
            <a-select-option value="">프롬프트를 선택하세요</a-select-option>
            <a-select-option 
              v-for="prompt in promptList" 
              :key="prompt.id" 
              :value="prompt.id"
            >
              {{ prompt.promptName }} ({{ prompt.promptCode }})
            </a-select-option>
          </a-select>
        </div>
      </div>
      
      <div class="form-row">
        <div class="form-group" style="flex: 1;">
          <label>제품명</label>
          <a-input
            v-model:value="form.productName"
            placeholder="제품명을 입력하세요"
          />
        </div>
        
        <div class="form-group" style="flex: 1.8;">
          <label>특별한 경험</label>
          <a-checkbox-group v-model:value="form.specialExperience">
            <div class="checkbox-grid">
              <a-checkbox value="통증이 거의 없었어요">통증이 거의 없었어요</a-checkbox>
              <a-checkbox value="수술이 빠르게 끝났어요">수술이 빠르게 끝났어요</a-checkbox>
              <a-checkbox value="의료진이 꼼꼼하게 수술했어요">의료진이 꼼꼼하게 수술했어요</a-checkbox>
              <a-checkbox value="전체 과정이 체계적이었어요">전체 과정이 체계적이었어요</a-checkbox>
              <a-checkbox value="수술 후 회복이 빨랐어요">수술 후 회복이 빨랐어요</a-checkbox>
              <a-checkbox value="수술 중 불안감이 없었어요">수술 중 불안감이 없었어요</a-checkbox>
              <a-checkbox value="사전 안내가 충분했어요">사전 안내가 충분했어요</a-checkbox>
              <a-checkbox value="예약이 원활했어요">예약이 원활했어요</a-checkbox>
              <a-checkbox value="설명이 자세하고 믿음이 갔어요">설명이 자세하고 믿음이 갔어요</a-checkbox>
              <a-checkbox value="수술 전 검사가 꼼꼼했어요">수술 전 검사가 꼼꼼했어요</a-checkbox>
            </div>
          </a-checkbox-group>
        </div>
      </div>
      
      <div class="form-row">
        <div class="form-group full-width">
          <label>사용자 프롬프트</label>
          <a-textarea
            v-model:value="form.userPrompt"
            placeholder="사용자 프롬프트"
            :rows="4"
            :disabled="true"
          />
        </div>
      </div>
      
      <div class="form-row">
        <div class="form-group full-width">
          <label>시스템 프롬프트</label>
          <a-textarea
            v-model:value="form.systemPrompt"
            placeholder="시스템 프롬프트"
            :rows="8"
            :disabled="true"
          />
        </div>
      </div>
      
      <div v-if="generatedReview" class="generated-review">
        <label>생성된 후기</label>
        <div class="review-content">
          {{ generatedReview }}
        </div>
      </div>
      
      <!-- 自定义按钮区域 -->
      <div class="modal-footer">
        <a-button @click="handleCancel">
          취소
        </a-button>
        <a-button 
          type="primary" 
          class="yellow-button"
          :loading="loading"
          @click="handleOk"
        >
          후기 생성
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { reactive, watch } from 'vue';
import { message } from 'ant-design-vue';

export default {
  name: 'ReviewTestModal',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    promptList: {
      type: Array,
      default: () => []
    },
    generatedReview: {
      type: String,
      default: ''
    },
    selectedPrompt: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['ok', 'cancel', 'promptSelect'],
  setup(props, { emit }) {
    // 폼 데이터
    const form = reactive({
      selectedPromptId: '',
      productName: '',
      specialExperience: [],
      userPrompt: '',
      systemPrompt: ''
    });

    // 폼 초기화
    const resetForm = () => {
      form.selectedPromptId = '';
      form.productName = '';
      form.specialExperience = [];
      form.userPrompt = '';
      form.systemPrompt = '';
    };

    // 프롬프트 선택 처리
    const handlePromptSelect = (promptId) => {
      const selectedPrompt = props.promptList.find(p => p.id === promptId);
      if (selectedPrompt) {
        form.userPrompt = selectedPrompt.userPrompt;
        form.systemPrompt = selectedPrompt.systemPrompt;
      }
      emit('promptSelect', promptId);
    };

    // 확인 버튼 클릭 (후기 생성)
    const handleOk = () => {
      if (!form.selectedPromptId) {
        message.warning('프롬프트를 선택해주세요.');
        return;
      }
      
      // 선택된 프롬프트 찾기
      const selectedPrompt = props.promptList.find(p => p.id === form.selectedPromptId);
      if (!selectedPrompt) {
        message.warning('선택된 프롬프트를 찾을 수 없습니다.');
        return;
      }
      
      const data = {
        promptCode: selectedPrompt.promptCode,
        promptItem: {
          serviceName: form.productName,
          specialExperience: form.specialExperience.join(', ')
        }
      };
      
      emit('ok', data);
    };

    // 취소 버튼 클릭
    const handleCancel = () => {
      resetForm();
      emit('cancel');
    };

    // selectedPrompt prop 변경 감지
    watch(() => props.selectedPrompt, (newPrompt) => {
      if (newPrompt && newPrompt.id) {
        form.selectedPromptId = newPrompt.id;
        form.userPrompt = newPrompt.userPrompt || '';
        form.systemPrompt = newPrompt.systemPrompt || '';
      }
    }, { immediate: true, deep: true });

    // open 변경 감지
    watch(() => props.open, (newOpen) => {
      if (!newOpen) {
        resetForm();
      }
    });

    return {
      form,
      handleOk,
      handleCancel,
      handlePromptSelect,
      resetForm
    };
  }
};
</script>

<style scoped lang="scss">
// 테스트 폼
.test-form {
  padding: 20px;
  .form-row {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  .form-group {
    flex: 1;
    
    &.full-width {
      flex: none;
      width: 100%;
    }
    
    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      font-size: 14px;
      color: #262626;
    }
    
    :deep(.ant-input),
    :deep(.ant-select),
    :deep(.ant-textarea) {
      border-radius: 6px;
    }
  }
}

// 체크박스 그리드
.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 20px;
  max-width: 100%;
  
  :deep(.ant-checkbox-wrapper) {
    display: flex !important;
    align-items: center !important;
    margin: 0 !important;
    padding: 10px 12px;
    border-radius: 6px;
    transition: background-color 0.2s;
    white-space: nowrap !important;
    flex-wrap: nowrap !important;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
    
    &:hover {
      background-color: #f5f5f5;
    }
    
    .ant-checkbox {
      margin-right: 8px !important;
      flex-shrink: 0 !important;
      display: inline-flex !important;
      align-items: center !important;
    }
    
    .ant-checkbox + span {
      line-height: 1.2 !important;
      display: inline !important;
      white-space: nowrap !important;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
      min-width: 0;
    }
    
    .ant-checkbox-wrapper + .ant-checkbox-wrapper {
      margin-left: 0 !important;
    }
  }
}

// 생성된 후기
.generated-review {
  margin-top: 20px;
  
  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 14px;
    color: #262626;
  }
  
  .review-content {
    padding: 16px;
    background-color: #f6f8fa;
    border-radius: 8px;
    border: 1px solid #e1e4e8;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
  }
}

// 모달 푸터
.modal-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 24px 0 0 0;
  margin-top: 24px;
  border-top: 1px solid #f0f0f0;
}

// 버튼 스타일
:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .checkbox-grid {
    grid-template-columns: 1fr;
    gap: 8px;
    
    :deep(.ant-checkbox-wrapper) {
      padding: 8px 10px;
      font-size: 14px;
    }
  }
  
  .modal-footer {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
}
</style> 